import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useRef, useState } from "react";
import type { DropTargetMonitor } from "react-dnd";
import { useDrag, useDrop } from "react-dnd";
import { shallowEqual, useSelector } from "react-redux";
import type {
  DimensionDescriptor,
  MeasureField,
  StatementLineType,
} from "../../../../../../../../api/types/svTypes.ts";
import { selectBuilderDetailedView } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { DEFAULT_AREA_FIELD_ITEM_HEIGHT } from "../../../viewer/common/fields/area-fields/AreaFieldsHelper.ts";
import CardConditionsButton from "../common/CardConditionsButton.tsx";
import CardDeleteButton from "../common/CardDeleteButton.tsx";
import CardOptionsButton from "../common/CardOptionsButton.tsx";
import { getFieldConditionCount } from "../utils/statementOptions.ts";
import type { LineDropTarget, StatementLineRail } from "../utils/statementLines.ts";
import { readsPeriodFromField } from "../utils/statementLines.ts";
import type { RowsDragItem, StatementLineDragItem } from "../utils/statementRowsArea.ts";
import { getBreakDownDimension, rowsAreaItemTypes, STATEMENT_LINE_ITEM_TYPE } from "../utils/statementRowsArea.ts";
import LineConditionsPanel from "./LineConditionsPanel.tsx";

const RAIL_WIDTH = 14;
const RAIL_LINE_LEFT = 6;
const RAIL_GAP_BRIDGE = 3;
const RAIL_ELBOW_TOP = DEFAULT_AREA_FIELD_ITEM_HEIGHT / 2;
const INSERT_ZONE_SHARE = 0.25;

const isPointerInInsertZone = (monitor: DropTargetMonitor, element: HTMLElement | null): boolean => {
  const pointer = monitor.getClientOffset();
  if (!pointer || !element) {
    return true;
  }
  const { top, height } = element.getBoundingClientRect();
  return pointer.y - top < height * INSERT_ZONE_SHARE;
};

interface DropCollectedProps {
  isOver: boolean;
  canBreakDown: boolean;
}

interface LineRailProps {
  continues: boolean;
  isLast: boolean;
}

const LineRail = ({ continues, isLast }: LineRailProps) => (
  <Box sx={{ position: "relative", width: RAIL_WIDTH, flexShrink: 0 }}>
    {(continues || isLast) && (
      <Box
        sx={{
          position: "absolute",
          left: RAIL_LINE_LEFT,
          top: -RAIL_GAP_BRIDGE,
          width: "1px",
          bgcolor: "divider",
          ...(continues ? { bottom: -RAIL_GAP_BRIDGE } : { height: RAIL_ELBOW_TOP + RAIL_GAP_BRIDGE }),
        }}
      />
    )}
    {isLast && (
      <Box
        sx={{
          position: "absolute",
          left: RAIL_LINE_LEFT,
          top: RAIL_ELBOW_TOP,
          width: RAIL_WIDTH - RAIL_LINE_LEFT,
          height: "1px",
          bgcolor: "divider",
        }}
      />
    )}
  </Box>
);

interface Props {
  guid: string;
  lineType: StatementLineType;
  label: string;
  hint: string;
  hintTooltip?: string;
  warning?: string;
  field: MeasureField | undefined;
  removeBlockedMessage?: string;
  rails: StatementLineRail[];
  selected: boolean;
  readonly: boolean;
  onSelect: (guid: string) => void;
  onDrop: (item: RowsDragItem, target: LineDropTarget) => void;
  onBreakDown: (dimension: DimensionDescriptor, guid: string) => void;
  onShowOptions: (guid: string, anchorEl: HTMLElement) => void;
  onRemove: (guid: string) => void;
}

const LineCard = ({
  guid,
  lineType,
  label,
  hint,
  hintTooltip,
  warning,
  field,
  removeBlockedMessage,
  rails,
  selected,
  readonly,
  onSelect,
  onDrop,
  onBreakDown,
  onShowOptions,
  onRemove,
}: Props) => {
  const [hovered, setHovered] = useState(false);
  const [conditionsAnchorEl, setConditionsAnchorEl] = useState<HTMLElement>();
  const [pointerInInsertZone, setPointerInInsertZone] = useState(true);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const detailedView = useSelector(selectBuilderDetailedView);
  const [{ isDragging }, drag] = useDrag<StatementLineDragItem, void, { isDragging: boolean }>(
    () => ({
      type: STATEMENT_LINE_ITEM_TYPE,
      item: { lineGuid: guid },
      canDrag: () => !readonly,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [guid, readonly]
  );
  const [{ isOver, canBreakDown }, drop] = useDrop<RowsDragItem, void, DropCollectedProps>(
    () => ({
      accept: rowsAreaItemTypes,
      canDrop: () => !readonly,
      hover: (_item, monitor) => setPointerInInsertZone(isPointerInInsertZone(monitor, cardRef.current)),
      drop: (item, monitor) => {
        const dimension = getBreakDownDimension(item, lineType);
        if (dimension && !isPointerInInsertZone(monitor, cardRef.current)) {
          onBreakDown(dimension, guid);
          return;
        }
        onDrop(item, { beforeGuid: guid });
      },
      collect: (monitor) => {
        const isOverCard = monitor.isOver({ shallow: true }) && monitor.canDrop();
        return {
          isOver: isOverCard,
          canBreakDown: isOverCard && getBreakDownDimension(monitor.getItem(), lineType) !== undefined,
        };
      },
    }),
    [guid, lineType, onBreakDown, onDrop, readonly]
  );
  const handleCardRef = useCallback(
    (element: HTMLDivElement | null) => {
      cardRef.current = element;
      drag(drop(element));
    },
    [drag, drop]
  );

  const hasField = readsPeriodFromField(lineType);
  const isSpacer = lineType === "Spacer";
  const isPlainLine = lineType === "Amount" || isSpacer;
  const conditionsField = hasField && field?.meta.capabilities.customConditionsAllowed ? field : undefined;
  const conditionCount = conditionsField ? getFieldConditionCount(conditionsField) : 0;
  const showInsertBar = canBreakDown && pointerInInsertZone;
  const showHoverActions = hovered && !readonly;
  const showConditionsButton =
    conditionsField !== undefined && (showHoverActions || conditionCount > 0 || conditionsAnchorEl !== undefined);

  return (
    <Stack direction="row">
      {rails.map((rail) => (
        <LineRail key={rail.level} continues={rail.continues} isLast={rail.level === rails.length - 1} />
      ))}
      <Stack
        ref={handleCardRef}
        onClick={() => onSelect(guid)}
        sx={(theme) => ({
          flex: 1,
          minWidth: 0,
          border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: "2px",
          bgcolor: "background.paper",
          outline: isOver && !showInsertBar ? `2px solid ${theme.palette.primary.main}` : "none",
          boxShadow: showInsertBar ? `0 -3px 0 0 ${theme.palette.primary.main}` : undefined,
          opacity: isDragging ? 0.5 : 1,
          cursor: readonly ? "pointer" : "grab",
        })}
      >
        <Stack
          direction="row"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{ minHeight: DEFAULT_AREA_FIELD_ITEM_HEIGHT, alignItems: "center" }}
        >
          <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: "center", gap: 0.75, px: 1.25 }}>
            {warning && (
              <Tooltip title={warning}>
                <WarningAmberRoundedIcon color="warning" />
              </Tooltip>
            )}
            <Typography
              variant="caption"
              noWrap
              color={isSpacer ? "textDisabled" : undefined}
              sx={{ flex: 1, minWidth: 0, fontWeight: isPlainLine ? "fontWeightRegular" : "fontWeightMedium" }}
            >
              {isSpacer ? hint : label}
            </Typography>
            {!showHoverActions && !isSpacer && (
              <Tooltip title={hintTooltip}>
                <Typography variant="caption" noWrap color={hasField ? "primary" : "textSecondary"}>
                  {hint}
                </Typography>
              </Tooltip>
            )}
          </Stack>
          {showHoverActions && (
            <CardDeleteButton blockedMessage={removeBlockedMessage} onDelete={() => onRemove(guid)} />
          )}
          {showConditionsButton && (
            <CardConditionsButton
              conditionCount={conditionCount}
              detailedView={detailedView}
              onClick={setConditionsAnchorEl}
            />
          )}
          <CardOptionsButton title="Line Options" onClick={(anchorEl) => onShowOptions(guid, anchorEl)} />
        </Stack>
        {conditionsField && (conditionsAnchorEl !== undefined || (detailedView && conditionCount > 0)) && (
          <LineConditionsPanel
            lineGuid={guid}
            field={conditionsField}
            readonly={readonly}
            popupAnchorEl={conditionsAnchorEl}
            onClosePopup={() => setConditionsAnchorEl(undefined)}
          />
        )}
      </Stack>
    </Stack>
  );
};

const areRailsEqual = (first: StatementLineRail[], second: StatementLineRail[]): boolean =>
  first.length === second.length &&
  first.every((rail, index) => rail.level === second[index]?.level && rail.continues === second[index]?.continues);

const areLineCardPropsEqual = (previous: Props, next: Props): boolean => {
  const { rails: previousRails, ...previousRest } = previous;
  const { rails: nextRails, ...nextRest } = next;
  return areRailsEqual(previousRails, nextRails) && shallowEqual(previousRest, nextRest);
};

export default memo(LineCard, areLineCardPropsEqual);
